import React, { useMemo } from 'react';
import { Persona } from '../../types/persona';
import { getCapabilityScore } from '../../utils/persona_engine_utils';

interface SkillHeatmapProps {
  personas: Persona[];
  maxValue?: number;
}

export const SkillHeatmap: React.FC<SkillHeatmapProps> = ({
  personas,
  maxValue = 10
}) => {
  // Extract all unique capabilities from all personas
  const capabilities = useMemo(() => {
    const allCapabilities = new Set<string>();
    
    personas.forEach(persona => {
      Object.keys(persona.capabilities || {}).forEach(capability => {
        allCapabilities.add(capability);
      });
    });
    
    return Array.from(allCapabilities).sort();
  }, [personas]);
  
  // Get color based on value
  const getHeatmapColor = (value: number) => {
    const percentage = (value / maxValue) * 100;
    
    if (percentage >= 90) return 'bg-green-900 text-white';
    if (percentage >= 80) return 'bg-green-800 text-white';
    if (percentage >= 70) return 'bg-green-700 text-white';
    if (percentage >= 60) return 'bg-green-600 text-white';
    if (percentage >= 50) return 'bg-green-500 text-white';
    if (percentage >= 40) return 'bg-green-400';
    if (percentage >= 30) return 'bg-green-300';
    if (percentage >= 20) return 'bg-green-200';
    if (percentage >= 10) return 'bg-green-100';
    return 'bg-gray-100';
  };
  
  if (personas.length === 0) {
    return (
      <div className="text-center py-6 bg-gray-50 rounded-lg">
        <p className="text-gray-500">No personas in the team to analyze</p>
      </div>
    );
  }
  
  return (
    <div className="skill-heatmap overflow-x-auto">
      <table className="min-w-full border-collapse">
        <thead>
          <tr>
            <th className="p-2 border bg-gray-50 sticky left-0 z-10">Persona</th>
            {capabilities.map(capability => (
              <th key={capability} className="p-2 border bg-gray-50 whitespace-nowrap">
                <div className="transform -rotate-45 origin-left translate-y-3 h-20 w-20">
                  <span className="capitalize">{capability}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {personas.map(persona => (
            <tr key={persona.id}>
              <td className="p-2 border font-medium sticky left-0 bg-white z-10">
                {persona.name}
              </td>
              {capabilities.map(capability => {
                const value = getCapabilityScore(persona.capabilities?.[capability]);
                return (
                  <td 
                    key={capability} 
                    className={`p-2 border text-center ${getHeatmapColor(value)}`}
                  >
                    {value > 0 ? value : '-'}
                  </td>
                );
              })}
            </tr>
          ))}
          <tr className="bg-gray-50 font-medium">
            <td className="p-2 border sticky left-0 bg-gray-50 z-10">Team Average</td>
            {capabilities.map(capability => {
              const sum = personas.reduce((acc, p) => acc + getCapabilityScore(p.capabilities?.[capability]), 0);
              const avg = sum / personas.length;
              return (
                <td 
                  key={capability} 
                  className={`p-2 border text-center ${getHeatmapColor(avg)}`}
                >
                  {avg.toFixed(1)}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
};
